import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { filesApi } from '@papan/files/client'
import { fileKeys, fetchAndCacheFileInfo } from '@papan/files/client/queries'
import { workspaceApi, getWorkspaceName, isDefinitiveResolveFailure, opensAsPlainFolder } from '@papan/workspace/client'
import type { WorkspaceLink, WorkspaceTabDef, FileOverride } from '@papan/workspace/types'
import { isRegisteredExtension as _isRegisteredExtension } from '@papan/collab/client'
import { PROJECT_FOLDER_EXT, MINIAPP_FOLDER_EXT, isMiniappFolder, miniappFolderExt } from '@papan/types'

export type WorkspaceEntityKind = 'project' | 'miniapp'

export interface Tab {
  id: string
  fileId: string     // Stable database ID (nanoid) — used for room naming
  title: string
  path: string       // Virtual path like "/papan/folder/file.csv" — for breadcrumb display
  type: string       // File extension (from editor registry) or 'unknown'
  isDirty: boolean
  isConflicted: boolean
  parentId?: string | null  // Parent folder ID (from file info)
  /** #823: the file was moved to the bin (it, or a folder above it) by someone else while open — the tab stays,
   *  greyed, so its last content can still be read and copied (a live editor shows the server's notice). */
  deleted?: boolean
  /** #823: bumped when a greyed tab's file comes back from the bin — its editor remounts (the old session ended). */
  restoredSeq?: number
}

export interface RecentFile {
  fileId: string
  name: string
  openedAt: number
}

function getFileType(name: string): string {
  const dotIdx = name.lastIndexOf('.')
  // No dot at all → treat as text file
  if (dotIdx < 0) return 'txt'
  // Dot-only files (e.g. ".") → text
  if (dotIdx === 0 && name.indexOf('.', 1) < 0) {
    // Dotfile with no extension (e.g. ".DS_Store", ".gitignore") — use the name as type
    // so it goes through binary detection instead of being misclassified as txt
    return name.slice(1).toLowerCase() || 'txt'
  }
  const ext = name.slice(dotIdx + 1).toLowerCase()
  return ext || 'txt'
}

let nextId = 0
function genId() {
  return `tab-${++nextId}-${Date.now()}`
}

interface UseWorkspaceOptions {
  /** The .workpapan folder ID */
  folderId?: string | null
  /** Whether this workspace is currently active/visible — skip fetches when false */
  isActive?: boolean
}

/** #1406 I3 — `configError` when the project was refused but the drive lets the person into its
 *  folder: the host leaves for `/folder/<id>` (the plain folder), as the drive opens it. */
export const PLAIN_FOLDER_CONFIG_ERROR = 'plain_folder'

export function useWorkspace(options?: UseWorkspaceOptions) {
  const propFolderId = options?.folderId ?? null
  const isActive = options?.isActive ?? true
  const [folderId, setFolderId] = useState<string | null>(propFolderId)
  const queryClient = useQueryClient()

  // Wrap filesApi.getFileInfo so the result lands in the shared TanStack Query
  // cache. TabContent/PermissionAwareEditor/etc. that later mount for the same
  // fileId hit `useFile`'s cache instead of firing their own request.
  // The helper also seeds the sibling permission + breadcrumbs caches from the
  // inlined detail response.
  const getFileInfoCached = useCallback(
    (fileId: string) => fetchAndCacheFileInfo(queryClient, fileId),
    [queryClient],
  )

  // Sync when URL param changes (e.g. navigating between workspaces)
  // Reset ALL workspace state so auto-save doesn't write stale data from
  // the previous workspace during the transition while the new config loads
  useEffect(() => {
    setFolderId(propFolderId)
    if (propFolderId && loadedFolderIdRef.current !== propFolderId) {
      setWorkspaceId(null)
      setWorkspaceName('')
      setEntityKind('project')
      setWorkspaceParentId(null)
      setTabs([])
      setActiveTabId(null)
      setLinks([])
      setFileOverrides([])
      setRecentFiles([])
      setConfigLoaded(false)
      hasLoadedRef.current = false
      recentFilesLoadedRef.current = false
      lastSavedRef.current = ''
    }
  }, [propFolderId])

  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [workspaceName, setWorkspaceName] = useState<string>('')
  const [workspacePath, setWorkspacePath] = useState<string | undefined>()
  // The kind of container this workspace folder is — drives the menu bar's
  // "Project" vs "MiniApp" labelling and the rename extension. Derived from the
  // on-disk folder name (`.miniapp` → miniapp, else project). Defaults to
  // project until the folder info loads.
  const [entityKind, setEntityKind] = useState<WorkspaceEntityKind>('project')
  // Parent folder id — used to reveal this workspace folder in the Drive.
  const [workspaceParentId, setWorkspaceParentId] = useState<string | null>(null)
  const [tabs, setTabs] = useState<Tab[]>([])
  const [activeTabId, setActiveTabId] = useState<string | null>(null)
  const [explorerWidth, setExplorerWidth] = useState(220)
  const [chatWidth, setChatWidth] = useState(320)
  const [explorerCollapsed, setExplorerCollapsed] = useState(false)
  const [chatCollapsed, setChatCollapsed] = useState(false)
  const [links, setLinks] = useState<WorkspaceLink[]>([])
  const [fileOverrides, setFileOverrides] = useState<FileOverride[]>([])
  const [configLoaded, setConfigLoaded] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)
  const [callerPermission, setCallerPermission] = useState<string | null>(null)
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([])

  // Track whether we've loaded config to avoid saving defaults on first render
  const hasLoadedRef = useRef(false)
  // Debounce timer for auto-save
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Track last-saved snapshot to avoid redundant writes
  const lastSavedRef = useRef<string>('')
  // Snapshot currently being saved (in-flight). New saves equal to this are skipped.
  const inflightSnapshotRef = useRef<string | null>(null)
  // Set when a save was requested while another was already in flight; we'll
  // re-run doSave after the in-flight completes to capture any newer state.
  const savePendingAfterFlightRef = useRef(false)
  // Track which folderId was loaded to avoid duplicate fetches (e.g. after ensureWorkspace)
  const loadedFolderIdRef = useRef<string | null>(null)
  // Flag: structural changes (links/tabs) trigger quick save (100ms) vs layout changes (5s)
  const saveImmediatelyRef = useRef(false)
  // File ids with an `openFile` in flight. A ref rather than state on purpose:
  // the second of two clicks in one tick has to see the first call's claim, and
  // a state update would not have committed by then — which is the same
  // staleness that let the duplicate tab through in the first place.
  const openingRef = useRef(new Set<string>())
  // Track whether recent files have been loaded from DB (skip initial save)
  const recentFilesLoadedRef = useRef(false)
  const recentSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Listen for external config changes (e.g. link added from popup)
  const [reloadTrigger, setReloadTrigger] = useState(0)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.folderId === folderId) {
        loadedFolderIdRef.current = null
        setReloadTrigger(prev => prev + 1)
      }
    }
    window.addEventListener('papan:workspace-changed', handler)
    return () => window.removeEventListener('papan:workspace-changed', handler)
  }, [folderId])

  // Load workspace config on mount when folderId is set
  useEffect(() => {
    if (!folderId) {
      // Reset all workspace state when navigating to empty workspace
      setWorkspaceId(null)
      setWorkspaceName('')
      setEntityKind('project')
      setWorkspaceParentId(null)
      setTabs([])
      setActiveTabId(null)
      setLinks([])
      setFileOverrides([])
      setRecentFiles([])
      setConfigError(null)
      setConfigLoaded(true)
      hasLoadedRef.current = true
      recentFilesLoadedRef.current = false
      loadedFolderIdRef.current = null
      lastSavedRef.current = ''
      return
    }

    // Defer fetch until workspace becomes active (avoids connection pool exhaustion)
    if (!isActive && !hasLoadedRef.current) return

    // Skip re-fetch if ensureWorkspace already set up this workspace
    if (loadedFolderIdRef.current === folderId) return

    // Reset loaded flag so consumers don't act on stale state (e.g. wrong workspace name)
    setConfigLoaded(false)

    let cancelled = false
    async function load() {
      try {
        const config = await workspaceApi.loadConfig(folderId!)
        if (cancelled) return

        // Sync workspace name from folder (user may have renamed .workpapan in DriveView)
        let displayName = config.name
        try {
          const folderInfo = await getFileInfoCached(folderId!)
          // Derive the container kind + parent from the live folder so the menu
          // bar labels correctly ("Project" vs "MiniApp") and Reveal-in-Drive
          // can locate this folder among its siblings.
          setEntityKind(isMiniappFolder(folderInfo.name) ? 'miniapp' : 'project')
          setWorkspaceParentId(folderInfo.parentId ?? null)
          const folderDerivedName = getWorkspaceName(folderInfo.name)
          if (folderDerivedName && folderDerivedName !== config.name) {
            displayName = folderDerivedName
            // Persist the updated name to DB (fire and forget)
            workspaceApi.saveConfig(config.id, { name: displayName }).catch(() => {})
          }
        } catch {
          // Folder lookup failed — use DB name
        }

        // Check if user has access to this workspace. #1406: the server returns a config only for a
        // project the caller can open (enter + read its config) — a 'hidden' level here is a hallway
        // project whose config they read, opened read-only. 'none' is no access.
        const perm = config.callerPermission
        if (perm === 'none') {
          setConfigError('no_access')
          setConfigLoaded(true)
          hasLoadedRef.current = true
          return
        }

        setWorkspaceId(config.id)
        setWorkspaceName(displayName)
        setWorkspacePath(config.path)
        setCallerPermission(perm ?? null)

        // Restore layout. Per-panel collapse superseded panelsHidden — fall
        // back to the legacy flag so an old "both hidden" workspace reopens
        // collapsed (with restore tabs) rather than fully expanded.
        // Guard against a partial/legacy layout that omits widths (or stores a
        // non-finite value): an undefined width unsets `style.width`, and the
        // resize math then computes `NaN` → panels can't be dragged. The server
        // now backfills defaults, but keep the floor here too (defense in depth,
        // and it repairs already-persisted `{}` rows — issue #255).
        const finiteOr = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)
        setExplorerWidth(finiteOr(config.layout.explorerWidth, 220))
        setChatWidth(finiteOr(config.layout.chatWidth, 320))
        setExplorerCollapsed(config.layout.explorerCollapsed ?? config.layout.panelsHidden ?? false)
        setChatCollapsed(config.layout.chatCollapsed ?? config.layout.panelsHidden ?? false)
        setLinks(config.links)
        setFileOverrides(config.fileOverrides ?? [])

        // Restore tabs — look up file names and paths for titles/breadcrumbs
        if (config.tabs.length > 0) {
          const restoredTabs: Tab[] = await Promise.all(
            config.tabs.map(async (tabDef: WorkspaceTabDef) => {
              let title = 'Unknown'
              let path = '/papan/'
              try {
                const info = await getFileInfoCached(tabDef.fileId)
                title = info.name
                path = info.path || `/papan/${info.name}`
              } catch {
                title = tabDef.fileId
                path = `/papan/${tabDef.fileId}`
              }
              return {
                id: genId(),
                fileId: tabDef.fileId,
                title,
                path,
                type: getFileType(title),
                isDirty: false,
              isConflicted: false,
              }
            })
          )
          if (cancelled) return
          setTabs(restoredTabs)
          const activeIdx = Math.min(
            config.layout.activeTabIndex ?? 0,
            restoredTabs.length - 1
          )
          setActiveTabId(restoredTabs[activeIdx]?.id ?? null)
        } else {
          setTabs([])
          setActiveTabId(null)
        }

        setConfigLoaded(true)
        setConfigError(null)
        hasLoadedRef.current = true
        loadedFolderIdRef.current = folderId!

        // Snapshot the initial state so we don't immediately re-save
        lastSavedRef.current = JSON.stringify({
          tabs: config.tabs.map(t => t.fileId),
          activeTabIndex: config.layout.activeTabIndex ?? 0,
          links: config.links.map(l => l.targetFolderId),
          layout: config.layout,
        })

        // Load recent files from user preferences
        try {
          const prefs = await workspaceApi.getPreferences(config.id)
          if (cancelled) return
          const stored = (prefs.settings?.recentFiles as Array<{ fileId: string; openedAt: number }>) || []
          if (stored.length > 0) {
            // Resolve file names (they may have changed)
            const resolved = await Promise.all(stored.map(async (entry) => {
              try {
                const info = await getFileInfoCached(entry.fileId)
                return { fileId: entry.fileId, name: info.name, openedAt: entry.openedAt }
              } catch {
                return null // File was deleted
              }
            }))
            if (cancelled) return
            setRecentFiles(resolved.filter((r): r is RecentFile => r !== null))
          }
        } catch {
          // Non-critical — recent files just won't load
        }
      } catch (err) {
        if (cancelled) return
        // #1406 I3: a project refused to this person (by-folder's 404 after its retries) that the
        // drive still lets them into is a plain folder to them — the host browses it
        // (`PLAIN_FOLDER_CONFIG_ERROR`), never "Failed to load".
        if (isDefinitiveResolveFailure(err) && await opensAsPlainFolder(folderId!)) {
          if (cancelled) return
          setConfigError(PLAIN_FOLDER_CONFIG_ERROR)
          setConfigLoaded(true)
          hasLoadedRef.current = true
          return
        }
        if (cancelled) return
        console.error('[useWorkspace] Failed to load config:', err)
        setConfigError('Failed to load workspace')
        setConfigLoaded(true)
        hasLoadedRef.current = true
      }
    }

    load()
    return () => { cancelled = true }
  }, [folderId, isActive, reloadTrigger])

  // Ref tracking latest state so flush-on-unmount can read current values
  const saveableRef = useRef({ workspaceId, tabs, activeTabId, links, explorerWidth, chatWidth, explorerCollapsed, chatCollapsed })
  saveableRef.current = { workspaceId, tabs, activeTabId, links, explorerWidth, chatWidth, explorerCollapsed, chatCollapsed }

  // Shared save function — reads from ref so it always has latest state.
  // Coalesces concurrent saves: while one PUT is in flight, additional save
  // requests set a "pending" flag and re-run doSave once the in-flight settles,
  // capturing the latest snapshot at that moment. This collapses bursts of
  // 9+ identical PUTs (observed in production) into 1–2 round trips.
  const doSave = useCallback(() => {
    const { workspaceId: wsId, tabs: t, activeTabId: atId, links: l,
            explorerWidth: ew, chatWidth: cw,
            explorerCollapsed: ec, chatCollapsed: cc } = saveableRef.current
    if (!wsId || !hasLoadedRef.current) return
    // Don't save while links are unresolved — relink endpoint handles it atomically
    if (l.some(link => link.missing)) return

    const activeIdx = t.findIndex(tab => tab.id === atId)
    const layout = {
      explorerWidth: ew,
      chatWidth: cw,
      explorerCollapsed: ec,
      chatCollapsed: cc,
      activeTabIndex: activeIdx >= 0 ? activeIdx : 0,
    }
    const tabDefs = t.map((tab, i) => ({ fileId: tab.fileId, sortOrder: i }))
    const linkDefs = l.map((link, i) => ({
      id: link.id, targetFolderId: link.targetFolderId, alias: link.alias, sortOrder: i,
      ...(link.path ? { path: link.path } : {}),
      permission: link.permission,
    }))

    const snapshot = JSON.stringify({
      tabs: tabDefs.map(td => td.fileId),
      activeTabIndex: layout.activeTabIndex,
      links: linkDefs.map(ld => ld.targetFolderId),
      layout,
    })
    if (snapshot === lastSavedRef.current) return
    if (snapshot === inflightSnapshotRef.current) return

    if (inflightSnapshotRef.current !== null) {
      // A save is already in flight; remember to re-run after it settles
      // so we don't lose changes that happened during the request.
      savePendingAfterFlightRef.current = true
      return
    }

    inflightSnapshotRef.current = snapshot
    workspaceApi.saveConfig(wsId, { layout, links: linkDefs, tabs: tabDefs })
      .then(() => { lastSavedRef.current = snapshot })
      .catch(err => { console.error('[useWorkspace] Auto-save failed:', err) })
      .finally(() => {
        inflightSnapshotRef.current = null
        if (savePendingAfterFlightRef.current) {
          savePendingAfterFlightRef.current = false
          // Re-snapshot from the latest ref values; if state hasn't moved,
          // this is a no-op via the equality checks above.
          doSave()
        }
      })
  }, [])

  // Auto-save on changes: structural changes (links/tabs/file open) → 100ms;
  // layout/active-tab tweaks → 5s. The longer window matters because users
  // tab-switch frequently and each switch bumps activeTabIndex; with a 2s
  // window every click could fire its own save.
  useEffect(() => {
    if (!workspaceId || !hasLoadedRef.current) return

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)

    const delay = saveImmediatelyRef.current ? 100 : 5000
    saveImmediatelyRef.current = false

    saveTimerRef.current = setTimeout(doSave, delay)

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [workspaceId, tabs, activeTabId, links, explorerWidth, chatWidth, explorerCollapsed, chatCollapsed, doSave])

  // Flush pending save when unmounting (e.g. navigating away)
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
        doSave()
      }
    }
  }, [doSave])

  // Persist recent files to user preferences (debounced)
  useEffect(() => {
    // Don't save until we've loaded config (avoid overwriting with empty array)
    if (!workspaceId || !hasLoadedRef.current) return
    // Skip the first render after load — the initial setRecentFiles from load shouldn't trigger a save
    if (!recentFilesLoadedRef.current) {
      recentFilesLoadedRef.current = true
      return
    }
    if (recentSaveTimerRef.current) clearTimeout(recentSaveTimerRef.current)
    recentSaveTimerRef.current = setTimeout(() => {
      const toSave = recentFiles.map(r => ({ fileId: r.fileId, openedAt: r.openedAt }))
      workspaceApi.saveRecentFiles(workspaceId, toSave).catch(() => {})
    }, 2000)
    return () => { if (recentSaveTimerRef.current) clearTimeout(recentSaveTimerRef.current) }
  }, [workspaceId, recentFiles])

  const openFile = useCallback(async (fileId: string, hint?: { name?: string; path?: string }) => {
    // ── Both guards run BEFORE any await, and that ordering is the fix. ──
    //
    // The already-open check used to sit after the metadata lookup. A double
    // click on a tree row fires `onClick` twice (the row opens on click, not on
    // dblclick), and the row supplies a name but no path — so every open took
    // the awaiting branch. Both calls suspended, both then read a `saveableRef`
    // that `setTabs` had not refreshed yet, both saw no tab, and both created
    // one: two identical tabs for one file.
    const existing = saveableRef.current.tabs.find(t => t.fileId === fileId)
    if (existing) {
      setActiveTabId(existing.id)
      setRecentFiles(prev => [
        { fileId, name: existing.title, openedAt: Date.now() },
        ...prev.filter(r => r.fileId !== fileId),
      ].slice(0, 10))
      return
    }

    // Moving the check alone is not enough: with no tab yet, two calls still
    // both pass it. This claims the id for the duration of the lookup, so the
    // second call returns and lets the first finish — which activates the tab
    // anyway, so nothing is lost by dropping it.
    if (openingRef.current.has(fileId)) return
    openingRef.current.add(fileId)

    try {
      // Use caller-provided name/path if available (works offline),
      // otherwise look up from server.
      let title = hint?.name ?? ''
      let path = hint?.path ?? ''
      if (!title || !path) {
        try {
          const info = await getFileInfoCached(fileId)
          if (!title) title = info.name
          if (!path) path = info.path || `/papan/${info.name}`
        } catch {
          if (!title) title = fileId
          if (!path) path = `/papan/${title}`
        }
      }

      // Re-check after the await: the claim above stops a concurrent `openFile`,
      // but a tab for this file can still have arrived by another route (a
      // restore, an agent action) while the lookup was in flight.
      const raced = saveableRef.current.tabs.find(t => t.fileId === fileId)
      if (raced) {
        setActiveTabId(raced.id)
      } else {
        const tab: Tab = {
          id: genId(),
          fileId,
          title,
          path,
          type: getFileType(title),
          isDirty: false,
          isConflicted: false,
        }
        saveImmediatelyRef.current = true
        setTabs(prev => [...prev, tab])
        setActiveTabId(tab.id)
      }

      // Track in recent files (after tab activation to avoid interfering with eager state)
      setRecentFiles(prev => {
        const filtered = prev.filter(r => r.fileId !== fileId)
        return [{ fileId, name: title, openedAt: Date.now() }, ...filtered].slice(0, 10)
      })
    } finally {
      openingRef.current.delete(fileId)
    }
  }, [])

  const closeTab = useCallback((tabId: string) => {
    saveImmediatelyRef.current = true
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === tabId)
      const next = prev.filter(t => t.id !== tabId)
      setActiveTabId(current => {
        if (current !== tabId) return current
        if (next.length === 0) return null
        const newIdx = Math.min(idx, next.length - 1)
        return next[newIdx].id
      })
      return next
    })
  }, [])

  /** Replace a tab's file in-place (same position, becomes active) */
  const replaceTab = useCallback(async (oldFileId: string, newFileId: string) => {
    let title: string
    let path: string
    try {
      const info = await getFileInfoCached(newFileId)
      title = info.name
      path = info.path || `/papan/${info.name}`
    } catch {
      title = newFileId
      path = `/papan/${newFileId}`
    }

    let newTabId: string | null = null
    setTabs(prev => {
      const idx = prev.findIndex(t => t.fileId === oldFileId)
      if (idx === -1) return prev
      const tab: Tab = {
        id: genId(),
        fileId: newFileId,
        title,
        path,
        type: getFileType(title),
        isDirty: false,
        isConflicted: false,
      }
      newTabId = tab.id
      saveImmediatelyRef.current = true
      const next = [...prev]
      next[idx] = tab
      return next
    })
    // Set active tab after setTabs so React processes both in sequence
    if (newTabId) {
      setActiveTabId(newTabId)
    }

    setRecentFiles(prev => {
      const filtered = prev.filter(r => r.fileId !== newFileId)
      return [{ fileId: newFileId, name: title, openedAt: Date.now() }, ...filtered].slice(0, 10)
    })
  }, [])

  const closeOtherTabs = useCallback((tabId: string) => {
    saveImmediatelyRef.current = true
    setTabs(prev => prev.filter(t => t.id === tabId))
    setActiveTabId(tabId)
  }, [])

  const closeTabsToRight = useCallback((tabId: string) => {
    saveImmediatelyRef.current = true
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === tabId)
      const next = prev.slice(0, idx + 1)
      setActiveTabId(current => {
        if (next.find(t => t.id === current)) return current
        return tabId
      })
      return next
    })
  }, [])

  const closeAllTabs = useCallback(() => {
    saveImmediatelyRef.current = true
    setTabs([])
    setActiveTabId(null)
  }, [])

  /** `movedId`/`targetId` are the tabs' ids AS OF DRAG START; the indices are
   *  a fallback for callers that have none. Resolve by ID where possible: the
   *  drag commits ~380ms after release, and a tab closed in another pane during
   *  the glide shifts every index after it, so the raw indices would splice the
   *  wrong file — silently, since both are valid positions. */
  const reorderTabs = useCallback((fromIndex: number, toIndex: number, ids?: { movedId: string; targetId: string }) => {
    saveImmediatelyRef.current = true
    setTabs(prev => {
      const from = ids?.movedId ? prev.findIndex(t => t.id === ids.movedId) : fromIndex
      const to = ids?.targetId ? prev.findIndex(t => t.id === ids.targetId) : toIndex
      // The list moved on and one of them is gone: drop the reorder rather than
      // splice at -1, which would send the tab to the end.
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev
      if (from === to) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }, [])

  const setTabDirty = useCallback((tabId: string, dirty: boolean) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId)
      if (!tab || tab.isDirty === dirty) return prev
      return prev.map(t => t.id === tabId ? { ...t, isDirty: dirty } : t)
    })
  }, [])

  /** #823: mark (or, on a restore, unmark) the tabs open on these files as moved to the bin. */
  /** Greys / un-greys tabs. Un-greying remounts the tab's editor (`restoredSeq`) unless `remount: false` — #823: only
   *  an editor whose session the server ENDED needs a fresh one; a live one may hold offline edits. */
  const setTabsDeleted = useCallback((fileIds: readonly string[], deleted: boolean, opts?: { remount?: boolean }) => {
    if (fileIds.length === 0) return
    const ids = new Set(fileIds)
    const remount = !deleted && opts?.remount !== false
    setTabs(prev => {
      if (!prev.some(t => ids.has(t.fileId) && !!t.deleted !== deleted)) return prev
      return prev.map(t => (ids.has(t.fileId) && !!t.deleted !== deleted
        ? { ...t, deleted, ...(remount ? { restoredSeq: (t.restoredSeq ?? 0) + 1 } : {}) }
        : t))
    })
  }, [])

  /** #823: a fresh editor in these tabs (a refused editor whose file the server says is back) — `restoredSeq` is the
   *  tab content's key. */
  const remountTabs = useCallback((fileIds: readonly string[]) => {
    if (fileIds.length === 0) return
    const ids = new Set(fileIds)
    setTabs(prev => (prev.some(t => ids.has(t.fileId))
      ? prev.map(t => (ids.has(t.fileId) ? { ...t, restoredSeq: (t.restoredSeq ?? 0) + 1 } : t))
      : prev))
  }, [])

  const setTabConflicted = useCallback((tabId: string, conflicted: boolean) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId)
      if (!tab || tab.isConflicted === conflicted) return prev
      return prev.map(t => t.id === tabId ? { ...t, isConflicted: conflicted } : t)
    })
  }, [])

  // Link management
  const addLink = useCallback((targetFolderId: string, alias?: string) => {
    const link: WorkspaceLink = {
      id: `link-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      targetFolderId,
      alias: alias ?? null,
      sortOrder: 0,
      permission: 'rw',
    }
    saveImmediatelyRef.current = true
    setLinks(prev => {
      // Don't add duplicate links
      if (prev.some(l => l.targetFolderId === targetFolderId)) return prev
      return [...prev, { ...link, sortOrder: prev.length }]
    })
  }, [])

  const removeLink = useCallback((linkId: string) => {
    saveImmediatelyRef.current = true
    setLinks(prev => prev.filter(l => l.id !== linkId))
  }, [])

  const renameLink = useCallback((linkId: string, alias: string | null) => {
    saveImmediatelyRef.current = true
    setLinks(prev => prev.map(l => l.id === linkId ? { ...l, alias } : l))
  }, [])

  /** Links that couldn't be resolved (target folder missing) */
  const missingLinks = useMemo(() => links.filter(l => l.missing), [links])

  /** Submit all relink decisions to server (atomic cascade + validate + write) */
  const submitRelinks = useCallback(async (decisions: Array<{ linkId: string; action: { type: 'relink'; newTargetFolderId: string } | { type: 'remove' } }>) => {
    if (!workspaceId) return

    const relinks: Array<{ linkIndex: number; newTargetFolderId: string }> = []
    const removals: number[] = []

    for (const { linkId, action } of decisions) {
      const linkIndex = links.findIndex(l => l.id === linkId)
      if (linkIndex < 0) continue
      if (action.type === 'relink') {
        relinks.push({ linkIndex, newTargetFolderId: action.newTargetFolderId })
      } else {
        removals.push(linkIndex)
      }
    }

    if (relinks.length === 0 && removals.length === 0) return

    try {
      const config = await workspaceApi.relinkFolders(workspaceId, relinks, removals)
      setLinks(config.links)
      setFileOverrides(config.fileOverrides ?? [])
      lastSavedRef.current = JSON.stringify({
        tabs: config.tabs.map((t: any) => t.fileId),
        activeTabIndex: config.layout.activeTabIndex ?? 0,
        links: config.links.map((l: any) => l.targetFolderId),
        layout: config.layout,
      })
    } catch (err) {
      console.error('[useWorkspace] Relink submit failed:', err)
    }
  }, [workspaceId, links])

  /** Remove a missing link */


  // Auto-create workspace: server creates .workpapan folder under .workspaces/ + DB record
  const ensureWorkspace = useCallback(async (): Promise<string | null> => {
    if (folderId) return folderId

    try {
      const { workspace: config, folderId: newFolderId } = await workspaceApi.createUntitled()

      // Set all state directly (avoid re-fetch)
      setFolderId(newFolderId)
      setWorkspaceId(config.id)
      setWorkspaceName(config.name)
      setWorkspacePath(config.path)
      setLinks(config.links)
      loadedFolderIdRef.current = newFolderId
      hasLoadedRef.current = true

      // Snapshot so auto-save doesn't immediately re-write defaults
      lastSavedRef.current = JSON.stringify({
        tabs: [],
        activeTabIndex: 0,
        links: [],
        layout: { explorerWidth, chatWidth, explorerCollapsed, chatCollapsed, activeTabIndex: 0 },
      })

      return newFolderId
    } catch (err) {
      console.error('[useWorkspace] Failed to auto-create workspace:', err)
      return null
    }
  }, [folderId, explorerWidth, chatWidth, explorerCollapsed, chatCollapsed])

  // Rename workspace: updates state, DB record, and project folder name.
  // Preserves the container kind's extension — mini-apps keep their EXISTING
  // suffix (`.miniapp`/`.pdapp`/`.pda`, PDApp v1 aliases) so a rename never
  // rewrites a `.pdapp`/`.pda` folder back to `.miniapp`; projects normalize to
  // the canonical `.project` (legacy `.workpapan` folders migrate forward on
  // rename; detection accepts both either way).
  const renameWorkspace = useCallback(async (newName: string) => {
    if (!workspaceId || !folderId || !newName.trim()) return
    const trimmed = newName.trim()
    setWorkspaceName(trimmed)
    let ext: string = PROJECT_FOLDER_EXT
    if (entityKind === 'miniapp') {
      // Read the current folder name so we keep its actual alias suffix rather
      // than forcing `.miniapp`; fall back to canonical `.miniapp` if unknown.
      try {
        const info = await getFileInfoCached(folderId)
        ext = miniappFolderExt(info.name) ?? MINIAPP_FOLDER_EXT
      } catch {
        ext = MINIAPP_FOLDER_EXT
      }
    }
    try {
      await workspaceApi.saveConfig(workspaceId, { name: trimmed })
      await filesApi.moveFile(folderId, { name: `${trimmed}${ext}` })
    } catch (err) {
      console.error('[useWorkspace] Rename failed:', err)
    }
  }, [workspaceId, folderId, entityKind, getFileInfoCached])

  // Refresh tab metadata (title, path, type) from server — used after file renames.
  // Forces a fresh fetch via invalidate, then writes the new value into the
  // shared cache so other consumers (TabContent, etc.) see it too.
  const refreshTabMeta = useCallback((fileId?: string) => {
    setTabs(prev => {
      const targets = fileId ? prev.filter(t => t.fileId === fileId) : prev
      if (targets.length === 0) return prev

      for (const tab of targets) {
        queryClient.invalidateQueries({ queryKey: fileKeys.detail(tab.fileId) })
        getFileInfoCached(tab.fileId).then(info => {
          setTabs(current => current.map(t => {
            if (t.fileId !== tab.fileId) return t
            const newType = getFileType(info.name)
            const newPath = info.path || `/papan/${info.name}`
            // Include `path` in the change check (#113 surface 1) — on a
            // pure move, name and type are unchanged but path is new;
            // without this the editor breadcrumb stays stale.
            if (t.title === info.name && t.type === newType && t.path === newPath) return t
            return { ...t, title: info.name, path: newPath, type: newType }
          }))
        }).catch(() => {})
      }
      return prev
    })
  }, [queryClient, getFileInfoCached])

  // ─── File override management ───────────────────────────────────────

  const aiOverrideMap = useMemo(() => {
    const map = new Map<string, 'hidden' | 'r'>()
    for (const o of fileOverrides) {
      if (o.principal === 'ai') {
        map.set(o.fileId, o.permission)
      }
    }
    return map
  }, [fileOverrides])

  const setAIAccess = useCallback(async (fileId: string, permission: 'hidden' | 'r') => {
    if (!workspaceId) return
    // Optimistic update
    setFileOverrides(prev => {
      const existing = prev.find(o => o.fileId === fileId && o.principal === 'ai')
      if (existing) {
        return prev.map(o => o.fileId === fileId && o.principal === 'ai' ? { ...o, permission } : o)
      }
      return [...prev, { id: `tmp-${Date.now()}`, fileId, principal: 'ai', permission }]
    })
    try {
      await workspaceApi.setFileOverride(workspaceId, fileId, 'ai', permission)
    } catch (err) {
      console.error('[useWorkspace] setAIAccess failed:', err)
      // Rollback on failure
      setFileOverrides(prev => prev.filter(o => !(o.fileId === fileId && o.principal === 'ai')))
    }
  }, [workspaceId])

  const clearAIAccess = useCallback(async (fileId: string) => {
    if (!workspaceId) return
    // Optimistic update
    const prev = fileOverrides
    setFileOverrides(current => current.filter(o => !(o.fileId === fileId && o.principal === 'ai')))
    try {
      await workspaceApi.removeFileOverride(workspaceId, fileId, 'ai')
    } catch (err) {
      console.error('[useWorkspace] clearAIAccess failed:', err)
      setFileOverrides(prev)
    }
  }, [workspaceId, fileOverrides])

  const activeTab = tabs.find(t => t.id === activeTabId) || null

  return {
    tabs,
    activeTabId,
    activeTab,
    openFile,
    replaceTab,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    closeAllTabs,
    reorderTabs,
    setActiveTabId,
    setTabDirty,
    setTabConflicted,
    explorerWidth,
    setExplorerWidth,
    chatWidth,
    setChatWidth,
    explorerCollapsed,
    setExplorerCollapsed,
    chatCollapsed,
    setChatCollapsed,
    // Workspace-specific
    folderId,
    workspaceId,
    workspaceName,
    workspacePath,
    entityKind,
    workspaceParentId,
    links,
    missingLinks,
    addLink,
    removeLink,
    renameLink,
    submitRelinks,
    renameWorkspace,
    ensureWorkspace,
    refreshTabMeta,
    setTabsDeleted,
    remountTabs,
    configLoaded,
    configError,
    // File overrides
    fileOverrides,
    aiOverrideMap,
    setAIAccess,
    clearAIAccess,
    recentFiles,
    callerPermission,
  }
}
